/**
 * «Статьи по теме» для карточки записи (БЗ-08): проект, работа графика, замечание, маршрут, вид работ, вид договора,
 * документ нормативной базы, смета. Вставляется в карточку одной строкой; если статей нет или читатель не вправе
 * видеть базу знаний, ничего не показывает.
 */
import type { KbArticleItem, KbLinkType } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { BookOpen } from 'lucide-react';
import { api } from '../../lib/api';
import { Panel } from '../../ui/layout';

export function RelatedArticles({
  type,
  id,
  className,
}: {
  type: KbLinkType;
  id: string;
  className?: string;
}) {
  const q = useQuery({
    queryKey: ['kb', 'for', type, id],
    queryFn: () => api<{ articles: KbArticleItem[] }>(`/kb/for/${type}/${encodeURIComponent(id)}`),
    retry: false,
  });
  if (!q.data?.articles.length) return null;
  return (
    <Panel
      className={className}
      title={
        <span className="inline-flex items-center gap-2">
          <BookOpen className="size-4" aria-hidden /> Статьи по теме
        </span>
      }
      bodyClassName="p-0"
    >
      {q.data.articles.map((a) => (
        <Link
          key={a.id}
          to="/kb/$articleId"
          params={{ articleId: a.id }}
          className="flex min-h-11 flex-col justify-center gap-0.5 border-b border-line px-4 py-2.5 last:border-b-0 hover:bg-tile/60"
        >
          <span className="text-[14px] font-medium text-ink [overflow-wrap:anywhere]">
            <span className="num mr-2 text-[12px] font-normal text-ink-2">{a.number}</span>
            {a.title}
          </span>
          {a.summary ? (
            <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{a.summary}</span>
          ) : null}
        </Link>
      ))}
    </Panel>
  );
}

/**
 * «Статьи по теме» в панели «Помощь» (БЗ-08): по адресу открытой страницы — статьи к записи (проект, замечание, смета,
 * документ нормативной базы) и к разделу. Так статьи видны в карточке любого раздела без правки его страниц.
 */
export function PathArticles({ path, onOpen }: { path: string; onOpen?: () => void }) {
  const q = useQuery({
    queryKey: ['kb', 'for-path', path],
    queryFn: () => api<{ articles: KbArticleItem[] }>('/kb/for-path', { query: { path } }),
    staleTime: 60_000,
    retry: false,
  });
  if (!q.data?.articles.length) return null;
  return (
    <section>
      <h4 className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold text-ink">
        <BookOpen className="size-4" aria-hidden /> Статьи по теме
      </h4>
      <ul className="flex flex-col">
        {q.data.articles.map((a) => (
          <li key={a.id}>
            <Link
              to="/kb/$articleId"
              params={{ articleId: a.id }}
              onClick={onOpen}
              className="flex min-h-11 flex-col justify-center gap-0.5 rounded-md px-2 py-2 hover:bg-tile/60"
            >
              <span className="text-[14px] font-medium text-ink [overflow-wrap:anywhere]">
                <span className="num mr-2 text-[12px] font-normal text-ink-2">{a.number}</span>
                {a.title}
              </span>
              {a.summary ? (
                <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{a.summary}</span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
